import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {
  selectHexes,
  selectPolicyMarket,
  selectUnits,
} from "../../redux/reducers/sharedStateReducer/selectors";
import {selectConstructionPlanEntries} from "../../redux/reducers/privateStateReducer/selectors";

// Окно схлопывания одинаковых звуков: "пулеметные" очереди одного тика
// (мгновенное распределение снабжения, массовый найм крысолюдов) озвучиваются одним звуком,
// а действия соседних тиков (BOT_ACTION_INTERVAL_MS >= 200мс) остаются озвученными по отдельности
const SAME_ACTION_EFFECT_THROTTLE_MS = 150;

const lastEffectTimestamps = {};

// playEffectForServerAction тянет за собой браузерные модули (компоненты с CSS),
// поэтому загружается динамически и только в браузере — headless-симуляции его не импортируют
let playEffectForServerActionFn = null;
let isEffectPlayerRequested = false;

// Первое действие бота в сессии приходит В ТОЙ ЖЕ синхронной задаче, в которой
// запрошен динамический импорт (createBotEffectsStore и первый dispatch внутри
// одного executeBotStep), поэтому модуля эффектов на нем гарантированно еще
// нет. Такие действия не проглатываются, а откладываются до резолва импорта —
// иначе первая же постройка бота проходила бы без звука и анимации роста
// города (в сетевой игре шаг бота исполняет случайный клиент-делегат, и его
// первым исполненным действием бывает именно постройка)
const pendingEffects = [];
// Дольше этого отложенный эффект уже неактуален: он читает состояние стора на
// момент проигрывания, а бот успевает сделать следующий ход (интервал 1500 мс)
const PENDING_EFFECT_MAX_AGE_MS = 1000;

/**
 * Забыть отложенные эффекты и историю троттлинга. Нужно при уходе с витрины
 * главного меню: отложенный эффект записи иначе отыграется уже в лобби или в
 * настоящем матче — со звуком и тостом чужой партии
 */
export const resetBotEffectBuffers = () => {
  pendingEffects.length = 0;
  Object.keys(lastEffectTimestamps).forEach((key) => {
    delete lastEffectTimestamps[key];
  });
};

const flushPendingEffects = () => {
  const now = Date.now();
  const effects = pendingEffects.splice(0, pendingEffects.length);
  effects.forEach(({args, queuedAt}) => {
    if (now - queuedAt > PENDING_EFFECT_MAX_AGE_MS) {
      return;
    }
    try {
      playEffectForServerActionFn(...args);
    } catch (error) {
      console.error('[BotEffects] Failed to play deferred action effect', error);
    }
  });
};

const ensureEffectPlayerLoaded = () => {
  if (isEffectPlayerRequested) {
    return;
  }
  isEffectPlayerRequested = true;
  import("../../scenes/playEffectForServerAction")
    .then((module) => {
      playEffectForServerActionFn = module.playEffectForServerAction;
      flushPendingEffects();
    })
    .catch((error) => {
      console.error('[BotEffects] Failed to load effect player', error);
      pendingEffects.length = 0;
    });
};

/**
 * SoundManager для действий бота: в браузере — тот же менеджер, которым озвучивается UI
 * (REGISTRY_SINGLETON.getSoundManager с fallback на менеджер меню), в headless-симуляциях
 * (Node, window отсутствует) — null, чтобы звуковой слой не включался.
 */
export const getBotSoundManager = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return REGISTRY_SINGLETON.getSoundManager() || null;
};

/**
 * Проигрывает звук/эффект для действия бота — так же, как playEffectForServerAction
 * озвучивает действия других игроков в сетевой игре.
 */
const playBotActionEffect = (store, soundManager, action, prevPolicyMarket, prevHexes, prevPlanEntries, prevUnits, prevState) => {
  try {
    const now = Date.now();
    const lastPlayed = lastEffectTimestamps[action.type];
    if (lastPlayed && now - lastPlayed < SAME_ACTION_EFFECT_THROTTLE_MS) {
      return;
    }
    lastEffectTimestamps[action.type] = now;

    if (playEffectForServerActionFn) {
      playEffectForServerActionFn(action, prevPolicyMarket, prevHexes, prevPlanEntries, prevUnits, prevState);
      return;
    }
    pendingEffects.push({args: [action, prevPolicyMarket, prevHexes, prevPlanEntries, prevUnits, prevState], queuedAt: now});
  } catch (error) {
    console.error('[BotEffects] Failed to play action effect', error);
  }
};

/**
 * Обертка store для решений бота: каждый dispatch дополнительно проигрывает
 * звук/тост действия. В headless-симуляциях (нет игровой сцены) возвращает store как есть.
 */
export const createBotEffectsStore = (store) => {
  // Песочница сильного режима: никаких звуков/эффектов от симулируемых действий
  if (store.__isBotSandbox) {
    return store;
  }
  const soundManager = getBotSoundManager();
  if (!soundManager) {
    return store;
  }

  ensureEffectPlayerLoaded();

  return {
    getState: () => store.getState(),
    dispatch: (action) => {
      // Рынок политик и hexes фиксируются до применения экшена — эффект покупки
      // политики берет название купленной политики из прежнего состояния рынка,
      // а проверка соседних гильдий/храмов на изменение дохода — hexes до
      // размещения нового строения (см. playNearbyGuildsTemplesIncomeChangeAnimation)
      const prevState = store.getState();
      const prevPolicyMarket = selectPolicyMarket(prevState);
      const prevHexes = selectHexes(prevState);
      // План строительства этого клиента: ход бота может снять его часть (см.
      // getPlannedGuildsAndTemplesIncomeChange)
      const prevPlanEntries = selectConstructionPlanEntries(prevState);
      // Отряды до действия: перемещение анимируется от исходной ячейки, а в
      // экшене ее нет (см. playUnitMovementAnimation)
      const prevUnits = selectUnits(prevState);
      const result = store.dispatch(action);
      // Состояние до действия целиком — по нему способности фракций
      // придерживают свой итог на поле до конца анимации
      playBotActionEffect(store, soundManager, action, prevPolicyMarket, prevHexes, prevPlanEntries, prevUnits, prevState);
      return result;
    },
  };
};
